import {
  useRef, useEffect, useState, useCallback, useMemo,
} from 'react'
import debounce from 'lodash-es/debounce'
import { fetchDepartmentTree, IDepartmentTreeNode } from '@/pages/staff/common/departmentApi'


/** 满足条件时执行一次, 之后不再更新了 */
export function useEffectOnece (cb: () => any, condition: boolean | number | string | undefined) {
  const executedRef = useRef(false)
  useEffect(() => {
    if (condition && !executedRef.current) {
      cb()
      executedRef.current = true
    }
  }, [condition])
}

export interface DeptOption {
  value: number;
  label: string;
  level: number;
  title: string;
}

/** 将部门树节点递归铺平为带层级信息的扁平选项（供 Selector 使用） */
const flattenDeptNodes = (nodes: IDepartmentTreeNode[], level = 0): DeptOption[] => {
  const result: DeptOption[] = []
  for (const node of nodes) {
    result.push({ value: node.id, label: node.name, level, title: node.name })
    if (node.children?.length) {
      result.push(...flattenDeptNodes(node.children, level + 1))
    }
  }
  return result
}

/**
 * 部门 Selector 分页加载 hook
 * - 搜索时重置分页，仅展示搜索结果
 * - 滚动到底部时加载下一页并追加
 */
export function useDeptOptions () {
  const PAGE_SIZE = 20

  const [options, setOptions] = useState<DeptOption[]>([])
  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const pageNoRef = useRef(1)
  const hasMoreRef = useRef(true)
  const loadingRef = useRef(false)

  const loadPage = useCallback(async (kw: string, pageNo: number, replace: boolean) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const res = await fetchDepartmentTree({ keyword: kw || undefined, pageNo, pageSize: PAGE_SIZE, searchMode: 'TREE' })
      const result = res.data?.result
      const totalPages = res.data?.totalPages ?? 1
      const nodes = Array.isArray(result) ? result : []
      const newOptions = flattenDeptNodes(nodes)
      setOptions(prev => replace ? newOptions : [...prev, ...newOptions])
      hasMoreRef.current = pageNo < totalPages
      pageNoRef.current = pageNo
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  // 初始加载
  useEffect(() => {
    loadPage('', 1, true)
  }, [])

  // 搜索：重置分页，替换 options
  const handleSearch = useCallback(
    debounce((kw: string) => {
      setKeyword(kw)
      pageNoRef.current = 1
      hasMoreRef.current = true
      loadPage(kw, 1, true)
    }, 300),
    [loadPage]
  )

  // 滚动到底部时加载下一页
  const handlePopupScroll = useCallback((el: HTMLElement) => {
    if (!hasMoreRef.current || loadingRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 40) {
      const nextPage = pageNoRef.current + 1
      loadPage(keyword, nextPage, false)
    }
  }, [keyword, loadPage])

  return { options, loading, handleSearch, handlePopupScroll }
}

export interface DeptTreeOption {
  value: number;
  /** 列表展示文本：始终为 name（组件自身展开能力处理层级，不做 namePath 平铺） */
  label: string;
  /** 选中后展示文本：始终为 namePath（部门链），无 namePath 时降级为 name */
  title?: string;
  /** 是否禁用（父级已选中时，子孙节点置为 disabled） */
  disabled?: boolean;
  children?: DeptTreeOption[];
}

/**
 * 将部门树节点递归转换为 TreeSelect 的树形 options
 * - label：始终展示 name，由 TreeSelect 组件自身展开能力处理层级，不手动平铺 namePath
 * - title：选中后展示 namePath（部门链），无 namePath 时回退为 name
 */
const mapDeptNodesToTreeOptions = (nodes: IDepartmentTreeNode[]): DeptTreeOption[] =>
  nodes.map(node => ({
    value: node.id,
    label: node.name,
    title: node.namePath || node.name,
    children: node.children?.length ? mapDeptNodesToTreeOptions(node.children) : undefined,
  }))

/**
 * 部门 TreeSelect 分页加载 hook（适配 @roo/roo0119 v1.19.4 新版 API）
 * - 懒加载：不自动初始化，由外部调用 init() 触发首次请求（弹窗打开时调用）
 * - showSearch 对象配置 onSearch，单框可搜索体验
 * - onPopupScroll 原生支持，无需 DOM 黑魔法
 * - 无搜索词时：label 展示 name，列表首位硬编码"无上级部门（一级部门）"（id=0）
 * - 有搜索词时：label 展示 namePath，不插入首位硬编码项
 * - 清空选择时调用 reset() 回到首页无关键词的数据
 * @param noParentLabel 首位固定项的展示文本（由外部传入 t() 结果，实现 i18n）
 */
export function useDeptTreeSelect (noParentLabel = '') {
  const PAGE_SIZE = 20

  const [treeOptions, setTreeOptions] = useState<DeptTreeOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const pageNoRef = useRef(1)
  const hasMoreRef = useRef(true)
  const loadingRef = useRef(false)
  const keywordRef = useRef('')

  /** 无搜索词时首位固定项（useMemo 确保引用稳定，避免 loadPage deps 告警） */
  const noParentOption = useMemo<DeptTreeOption>(() => ({ value: 0, label: noParentLabel }), [noParentLabel])

  const loadPage = useCallback(async (kw: string, pageNo: number, replace: boolean) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const res = await fetchDepartmentTree({ keyword: kw || undefined, pageNo, pageSize: PAGE_SIZE, searchMode: 'TREE' })
      const result = res.data?.result
      const totalPages = res.data?.totalPages ?? 1
      const nodes = Array.isArray(result) ? result : []
      // label 始终展示 name，由 TreeSelect 组件自身展开能力处理层级
      const newOptions = mapDeptNodesToTreeOptions(nodes)
      setTreeOptions(prev => {
        if (replace) {
          return (kw || !noParentLabel) ? newOptions : [noParentOption, ...newOptions]
        }
        // 追加时无需再插入首位（首位已在首次加载时插入）
        return [...prev, ...newOptions]
      })
      hasMoreRef.current = pageNo < totalPages
      pageNoRef.current = pageNo
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [noParentLabel, noParentOption])

  /** 弹窗打开时调用，触发首次加载（若已有数据则重置到第一页） */
  const init = useCallback(() => {
    keywordRef.current = ''
    pageNoRef.current = 1
    hasMoreRef.current = true
    loadPage('', 1, true)
  }, [loadPage])

  /** 清空选择时调用，重置关键词并重新加载第一页 */
  const reset = useCallback(() => {
    keywordRef.current = ''
    pageNoRef.current = 1
    hasMoreRef.current = true
    setIsSearching(false)
    loadPage('', 1, true)
  }, [loadPage])

  // 搜索回调：重置分页，替换整棵树（供 showSearch.onSearch 使用）
  // 搜索有词时 isSearching=true，外部可配合 treeDefaultExpandAll 自动展开搜索结果
  const handleSearch = useCallback(
    debounce((kw: string) => {
      keywordRef.current = kw
      pageNoRef.current = 1
      hasMoreRef.current = true
      setIsSearching(Boolean(kw))
      loadPage(kw, 1, true)
    }, 300),
    [loadPage]
  )

  // 弹层滚动到底时加载下一页（供 onPopupScroll 使用，v1.19.0+ 原生支持）
  const handlePopupScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (!el || !hasMoreRef.current || loadingRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 40) {
      const nextPage = pageNoRef.current + 1
      loadPage(keywordRef.current, nextPage, false)
    }
  }, [loadPage])

  return { treeOptions, loading, isSearching, init, reset, handleSearch, handlePopupScroll }
}

export interface CostCenterOption {
  value: number | string;
  label: string;
}

/**
 * 成本中心 Selector 服务端搜索 + 滚动分页加载 hook
 * - 懒加载：不自动初始化，由外部调用 init() 触发首次请求（弹窗打开时调用）
 * - handleSearch：输入关键字后防抖 300ms 触发搜索，重置到第 1 页
 * - handlePopupScroll：滚动到底部时自动加载下一页并追加
 * @param valueField 'id'（默认）时 value 为成本中心 id；'name' 时 value 为成本中心名称（用于订单搜索等仅需传名称的场景）
 */
export function useCostCenterOptions (valueField: 'id' | 'name' = 'id') {
  const PAGE_SIZE = 20

  const [options, setOptions] = useState<CostCenterOption[]>([])
  const [loading, setLoading] = useState(false)
  const pageNoRef = useRef(1)
  const hasMoreRef = useRef(true)
  const loadingRef = useRef(false)
  const keywordRef = useRef('')

  // 动态 import 避免循环依赖（cost-center/api → hooks 是单向的）
  const loadPage = useCallback(async (kw: string, pageNo: number, replace: boolean) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const { fetchCostCenterList } = await import('@/pages/cost-center/api')
      const res = await fetchCostCenterList({ keyword: kw || undefined, pageNo, pageSize: PAGE_SIZE })
      const result = res.data?.result ?? []
      const totalCount = res.data?.totalCount ?? 0
      const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1
      const newOptions: CostCenterOption[] = result.map(item => ({ value: valueField === 'name' ? item.name : item.id, label: item.name }))
      setOptions(prev => replace ? newOptions : [...prev, ...newOptions])
      hasMoreRef.current = pageNo < totalPages
      pageNoRef.current = pageNo
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [valueField])

  /** 弹窗/页面打开时调用，触发首次加载（若已有数据则重置到第一页） */
  const init = useCallback(() => {
    keywordRef.current = ''
    pageNoRef.current = 1
    hasMoreRef.current = true
    loadPage('', 1, true)
  }, [loadPage])

  /** 搜索：重置分页，替换 options */
  const handleSearch = useCallback(
    debounce((kw: string) => {
      keywordRef.current = kw
      pageNoRef.current = 1
      hasMoreRef.current = true
      loadPage(kw, 1, true)
    }, 300),
    [loadPage]
  )

  /** 弹层滚动到底时加载下一页 */
  const handlePopupScroll = useCallback((el: HTMLElement) => {
    if (!hasMoreRef.current || loadingRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 40) {
      const nextPage = pageNoRef.current + 1
      loadPage(keywordRef.current, nextPage, false)
    }
  }, [loadPage])

  return { options, loading, init, handleSearch, handlePopupScroll }
}

export interface ExpenseCodeOption {
  value: string;
  label: string;
}

/**
 * 费用代码 Selector 服务端搜索 + 滚动分页加载 hook
 * - 懒加载：不自动初始化，由外部调用 init() 触发首次请求（弹窗/页面打开时调用）
 * - handleSearch：输入关键字后防抖 300ms 触发搜索，重置到第 1 页
 * - handlePopupScroll：滚动到底部时自动加载下一页并追加
 * - value/label 均使用 code（费用代码），便于作为过滤参数直接传给后端
 */
export function useExpenseCodeOptions () {
  const PAGE_SIZE = 20

  const [options, setOptions] = useState<ExpenseCodeOption[]>([])
  const [loading, setLoading] = useState(false)
  const pageNoRef = useRef(1)
  const hasMoreRef = useRef(true)
  const loadingRef = useRef(false)
  const keywordRef = useRef('')

  const loadPage = useCallback(async (kw: string, pageNo: number, replace: boolean) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const { fetchExpenseCodeList } = await import('@/pages/expense-code/api')
      const res = await fetchExpenseCodeList({ keyword: kw || undefined, pageNo, pageSize: PAGE_SIZE })
      const result = res.data?.result ?? []
      const totalCount = res.data?.totalCount ?? 0
      const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1
      const newOptions: ExpenseCodeOption[] = result.map(item => ({ value: item.code, label: item.code }))
      setOptions(prev => replace ? newOptions : [...prev, ...newOptions])
      hasMoreRef.current = pageNo < totalPages
      pageNoRef.current = pageNo
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  /** 弹窗/页面打开时调用，触发首次加载（若已有数据则重置到第一页） */
  const init = useCallback(() => {
    keywordRef.current = ''
    pageNoRef.current = 1
    hasMoreRef.current = true
    loadPage('', 1, true)
  }, [loadPage])

  /** 搜索：重置分页，替换 options */
  const handleSearch = useCallback(
    debounce((kw: string) => {
      keywordRef.current = kw
      pageNoRef.current = 1
      hasMoreRef.current = true
      loadPage(kw, 1, true)
    }, 300),
    [loadPage]
  )

  /** 弹层滚动到底时加载下一页 */
  const handlePopupScroll = useCallback((el: HTMLElement) => {
    if (!hasMoreRef.current || loadingRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop - clientHeight < 40) {
      const nextPage = pageNoRef.current + 1
      loadPage(keywordRef.current, nextPage, false)
    }
  }, [loadPage])

  return { options, loading, init, handleSearch, handlePopupScroll }
}
